"use client"

import React, { Fragment, useState, useEffect, useMemo } from 'react';
import { useRouter, usePathname, useSearchParams } from 'next/navigation';
import { Waypoint } from 'react-waypoint';
import * as Sentry from "@sentry/nextjs";
import GameSearchBar from './GameSearchBar';
import FilterBar from './FilterBar';
import Recommendation from './Recommendation';
import { FullGameCard, SkeletonCard } from './FullGameCard';
import { Button, Drawer, DrawerOverlay, DrawerContent, DrawerBody, Skeleton } from '@chakra-ui/react'
import { MdTune } from "react-icons/md";
import { legalFullQueryList, trackEvent } from 'utils';
import { getGamesList, stringifyQuery } from 'src/api';
import { useUser, useGlobal } from 'src/context';

const initialQuery = {
  q: '',
  city: '',
  ft: '',
  pp: '',
  price: '',
  storeId: '',
  outdoor: true,
  outdoor_only: false,
  big: false,
  over: false,
  over_only: false,
  rand: '',
  orderby: 'count',
}

const boolFields = ["outdoor", "outdoor_only", "big", "over", "over_only"];

const skeletonArray = size => Array(size).fill('');

const GamesListLayout = (props) => {
  const { 
    gamesListCount: initialGamesListCount,
    gamesListData: initialGamesListData,
  } = props;

  const [isFirstRenderReady, setIsFirstRenderReady] = useState(false);
  const [isLoadingNew, setIsLoadingNew] = useState(false); // 設定新條件，清除既有結果並顯示 skeleton
  const [isLoadingMore, setIsLoadingMore] = useState(false) // 既定條件取更多，不清除既有結果並顯示 skeleton
  const [isSearchBarOpen, setIsSearchBarOpen] = useState(false); // 手機版開啟搜尋條件抽屜
  const [hasManualCondition, setHasManualCondition] = useState(false); // 手機版提示現在有設定搜尋條件
  const [isHidingReviewed, setIsHidingReviewed] = useState(false); // 隱藏已經玩過的
  const [currentChosenRecommendation, setCurrentChosenRecommendation] = useState(''); // 現正顯示快速推薦，顯示於 FilterBar
  const [gamesListCount, setGamesListCount] = useState(0);
  const [gamesListData, setGamesListData] = useState([]);
  const [query, setQuery] = useState(initialQuery);
  const [currentPage, setCurrentPage] = useState(1);

  const { isScreenLg } = useGlobal();

  const { 
    user: { reviewedGamesIdList },
  } = useUser();

  const router = useRouter()
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const hasNoResult = useMemo(() => {
    return gamesListCount === 0;
  }, [gamesListCount]);

  useEffect(() => {
    if (initialGamesListCount) setGamesListCount(initialGamesListCount);
    if (initialGamesListData) setGamesListData(initialGamesListData);

    let routerQuery = {}
    searchParams.forEach((value, key) => {
      if (legalFullQueryList.includes(key)) {
        routerQuery[key] = value
      }
    })
    if (Object.keys(routerQuery).length) {
      // 過濾可用條件
    //   const newRouterQuery = Object.keys(routerQuery)
    //     .filter((item) => legalFullQueryList.includes(item))
    //     .reduce((queryObj, item) => {
    //       return { ...queryObj, [item]: routerQuery[item] };
    //     }, {});

      // 完成搜尋時將 prnew 與 prmo 去除，避免再次搜尋時會將此參數帶入
      const { prmo, prnew, rand, ...queries } = routerQuery;

      if (!!prmo) {
        setCurrentChosenRecommendation("本月推薦")
      } else if (!!prnew) {
        setCurrentChosenRecommendation("近期新作") 
      } else if (!!rand) {
        setCurrentChosenRecommendation("隨機探索") 
      }
      
      const newQueries = {
        ...initialQuery,
        ...queries,
      }

      boolFields.forEach(field => {
        if (field in routerQuery) {
          newQueries[field] = true;
        }
      })

      setQuery(newQueries)
    } else {
      // 沒給 query 時已經在 SSR 階段取本月推薦，UI 也要對應呈現字
      setCurrentChosenRecommendation("本月推薦")
    }

    setIsFirstRenderReady(true);
  }, []);


  /**
   * @param {object} incomingQuery 會以 incomingQuery 結合現有 query 送出查詢，並在查完後覆蓋 query。
   * @param {boolean} isReset 若有此項，會無視現在的 query ，直接以 incomingQuery 送出，用於快速推薦
   */
  const handleLoad = async (incomingQuery = null, isReset = false) => {
    const newQuery = isReset ? {
      ...initialQuery,
      ...incomingQuery,
    } : {
      ...query,
      ...incomingQuery,
      outdoor_only: false,
      page: '1'
    }

    setIsLoadingNew(true);

    try {
      const res = await getGamesList(newQuery);
      const { gamesListCount, gamesListData } = res.data;

      setGamesListCount(gamesListCount);
      setGamesListData(gamesListData);
    } catch (err) {
      console.log(err);
      Sentry.captureException(err);
      setGamesListCount(0);
      setGamesListData([]);
    } finally {
      setIsLoadingNew(false);
      setQuery(newQuery)
      setCurrentPage(1);
      if (!isReset) setCurrentChosenRecommendation("")

      router.push(`${pathname}?${stringifyQuery(newQuery).substring(1)}`);
    }
  };

  const handleSearchClick = (query) => {
    trackEvent({
      category: "GamesList-Search",
      action: "Search",
      label: stringifyQuery(query),
    });

    if (isSearchBarOpen) handleSearchBarClose()

    // 按下搜尋按鈕的無論如何都是第一頁，因為第二頁以後應該要用 handleLoadMore
    handleLoad(query);
  };

  const handleLoadMore = async () => {
    if (gamesListData.length >= gamesListCount) return;

    const newQuery = {
      ...query,
      page: (currentPage + 1).toString()
    }

    setIsLoadingMore(true);

    try {
      const res = await getGamesList(newQuery);
      const { gamesListCount: newGamesListCount, gamesListData: newGamesListData } = res.data;

      setGamesListCount(newGamesListCount);
      setGamesListData([...gamesListData, ...newGamesListData]);
      setCurrentPage(prev => prev + 1);
    } catch (err) {
      console.log(err);
      Sentry.captureException(err);
    } finally {
      setIsLoadingMore(false);
    }
  };

  const handleRecommendationSelect = (incomingQuery, type) => {
    // 搜尋時會清除現正顯示 ，在此才放入
    handleLoad(incomingQuery, true)
    setCurrentChosenRecommendation(type)
  };

  const handleSearchBarOpen = () => {
    setIsSearchBarOpen(true);
  };

  const handleSearchBarClose = () => {
    setIsSearchBarOpen(false);
  };

  const handleHideReviewedToggle = () => {
    setIsHidingReviewed(prev => !prev);
  };

  return (
    <>
        <div className='w-full p-2 md:p-4 flex flex-col lg:flex-row'>
          <div className='mb-2 lg:m-4 lg:ml-0 lg:w-64'>
            {isFirstRenderReady ?
              isScreenLg ? (
                <GameSearchBar
                  query={query}
                  onSearch={handleSearchClick}
                  onConditionChange={setHasManualCondition}
                  isHidingReviewed={isHidingReviewed}
                  onHideToggle={handleHideReviewedToggle}
                />
              ) : (
                <>
                  <div className='w-full border-b border-b-gray-700' onClick={handleSearchBarOpen}>
                    <Button
                      variant='ghost'
                      size='sm'
                      colorScheme={hasManualCondition ? "secondary" : "primary"}
                      leftIcon={<MdTune />}
                      w='full'
                    >
                      搜尋條件與排序方式
                    </Button>
                  </div>
                  <Drawer
                    isOpen={isSearchBarOpen}
                    placement='bottom'
                    onClose={handleSearchBarClose} 
                  >
                    <DrawerOverlay />
                    <DrawerContent>
                      <DrawerBody
                        maxH='80vh'
                        pt={0}
                      >
                        <GameSearchBar
                          query={query}
                          onSearch={handleSearchClick}
                          onConditionChange={setHasManualCondition}
                          isHidingReviewed={isHidingReviewed}
                          onHideToggle={handleHideReviewedToggle}
                        />
                      </DrawerBody>
                    </DrawerContent>
                  </Drawer>
                </>
              ) : (
                <Skeleton height={{ base: '32px', lg: '600px' }} />
              )}
          </div>
          <div className='md:mt-4 flex-grow'>
            {isFirstRenderReady ? (
              hasNoResult ? (
                <h6 className='mb-4'>
                  沒有符合您搜尋條件的結果，可以放寬搜尋條件或者利用以下熱門分類
                </h6>
              ) : (
                <Fragment>
                  <FilterBar
                    query={query}
                    count={gamesListCount}
                    currentChosenRecommendation={currentChosenRecommendation}
                    isHidingReviewed={isHidingReviewed}
                  />
                  {isLoadingNew && <SkeletonGroup />}
                  <div className='mb-4 grid grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-3 md:gap-4'>
                    {gamesListData.map((gameObj, idx) => {
                      const hasReviewed = reviewedGamesIdList.includes(gameObj.gameId);
                      
                      if (hasReviewed && isHidingReviewed) return null;
                      
                      return (
                        <FullGameCard 
                          key={`${gameObj.gameId}-${idx}`}
                          gameData={gameObj}
                          sourcePage="GamesList"
                        />
                      )
                    })}
                      {!isLoadingMore && (
                        <Waypoint onEnter={handleLoadMore}>
                          <div />
                        </Waypoint>
                      )}
                    </div>
                    {isLoadingMore && <SkeletonGroup />}
                  </Fragment>
                )
            ) : <SkeletonGroup />}
            <Recommendation handleSelect={handleRecommendationSelect} />
          </div>
        </div>
    </>
  );
};

const SkeletonGroup = () => {
  const { isScreenLg } = useGlobal();

  return (
    <div className='mb-4 grid grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-3 md:gap-4'>
      {skeletonArray(isScreenLg ? 6 : 4).map((skeleton, idx) => {
        return <SkeletonCard key={`ske-${idx}`} />
      })}
    </div>
  )
}

export default GamesListLayout;